// EditMedicalProfessional.js - FIXED with proper data handling

import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  TextField,
  LinearProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";
import { useAuth } from "../../contexts/AuthContext";

const EditMedicalProfessional = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageMedicalProfessionals } = useAuth();

  const [medicalProfessional, setMedicalProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // FIXED: Form data state with proper initialization
  const [formData, setFormData] = useState({
    mp_category: "",
    medical_license_number: "",
    medical_license_expiry: "",
    npi_number: "",
    drivers_license_number: "",
    drivers_license_expiry: "",
    vehicle_number: "",
    vehicle_type: "",
    vehicle_make: "",
    vehicle_model: "",
    license_plate: "",
    geographic_area: {},
    is_active: true,
    can_work_weekends: false,
    can_work_evenings: false,
    max_daily_orders: 10,
  });

  // FIXED: Starting point data state
  const [startingPointData, setStartingPointData] = useState({
    name: "",
    address: "",
    type: "home",
    lat: null,
    lng: null,
  });

  // Categories and vehicle types
  const categories = [
    { value: "nurse", label: "Nurse" },
    { value: "doctor", label: "Doctor" },
    { value: "technician", label: "Technician" },
    { value: "phlebotomist", label: "Phlebotomist" },
    { value: "other", label: "Other" },
  ];

  const vehicleTypes = [
    { value: "", label: "Not specified" },
    { value: "sedan", label: "Sedan" },
    { value: "suv", label: "SUV" },
    { value: "van", label: "Van" },
    { value: "truck", label: "Truck" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    if (!canManageMedicalProfessionals) {
      setError("You don't have permission to edit medical professionals");
      setLoading(false);
      return;
    }

    fetchMedicalProfessionalDetails();
  }, [id, canManageMedicalProfessionals]);

  const fetchMedicalProfessionalDetails = async () => {
    setLoading(true);
    try {
      const response =
        await medicalProfessionalsService.getMedicalProfessionalById(id);
      if (response) {

        setMedicalProfessional(response);

        // FIXED: Properly set form data with safe defaults and proper date handling
        setFormData({
          mp_category: response.mp_category || "",
          medical_license_number: response.medical_license_number || "",
          medical_license_expiry: response.medical_license_expiry
            ? response.medical_license_expiry.split("T")[0]
            : "",
          npi_number: response.npi_number || "",
          drivers_license_number: response.drivers_license_number || "",
          drivers_license_expiry: response.drivers_license_expiry
            ? response.drivers_license_expiry.split("T")[0]
            : "",
          vehicle_number: response.vehicle_number || "",
          vehicle_type: response.vehicle_type || "",
          vehicle_make: response.vehicle_make || "",
          vehicle_model: response.vehicle_model || "",
          license_plate: response.license_plate || "",
          geographic_area: response.geographic_area || {},
          is_active: response.user?.is_active ?? true,
          can_work_weekends: response.can_work_weekends || false,
          can_work_evenings: response.can_work_evenings || false,
          max_daily_orders: response.max_daily_orders || 10,
        });

        // FIXED: Properly load starting point data
        if (
          response.geographic_area &&
          typeof response.geographic_area === "object"
        ) {
          const startingPoint = response.geographic_area.starting_point;
          if (startingPoint) {
            setStartingPointData({
              name: startingPoint.name || "",
              address: startingPoint.address || "",
              type: startingPoint.type || "home",
              lat: startingPoint.lat || null,
              lng: startingPoint.lng || null,
            });
          }
        }
      } else {
        setError("Medical professional not found");
      }
    } catch (err) {
      console.error("Error fetching medical professional details:", err);
      setError("Failed to load medical professional details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStartingPointChange = (e) => {
    const { name, value } = e.target;

    setStartingPointData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // FIXED: Data cleaning utility function
  const cleanUpdateData = (data) => {
    const cleaned = {};

    Object.keys(data).forEach((key) => {
      const value = data[key];

      // Handle string fields - convert empty strings to null
      if (typeof value === "string") {
        const trimmed = value.trim();
        cleaned[key] = trimmed === "" ? null : trimmed;
      }
      // Handle boolean fields
      else if (typeof value === "boolean") {
        cleaned[key] = value;
      }
      // Handle numeric fields
      else if (typeof value === "number") {
        cleaned[key] = value;
      }
      // Handle null/undefined
      else if (value === null || value === undefined) {
        cleaned[key] = null;
      }
      // Handle objects (like geographic_area)
      else if (typeof value === "object") {
        cleaned[key] = value;
      }
      // Default case
      else {
        cleaned[key] = value;
      }
    });

    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {

      // FIXED: Properly build geographic_area with starting point
      const geographic_area = { ...formData.geographic_area };

      // Add starting point if address is provided
      if (startingPointData.address && startingPointData.address.trim()) {
        geographic_area.starting_point = {
          name: startingPointData.name || startingPointData.address,
          address: startingPointData.address.trim(),
          type: startingPointData.type || "home",
          updated_at: new Date().toISOString(),
        };

        // Add coordinates if available
        if (startingPointData.lat !== null && startingPointData.lng !== null) {
          geographic_area.starting_point.lat = parseFloat(
            startingPointData.lat
          );
          geographic_area.starting_point.lng = parseFloat(
            startingPointData.lng
          );
        }
      } else {
        // Remove starting_point if no address provided
        delete geographic_area.starting_point;
      }

      // FIXED: Build update data with proper cleaning
      const rawUpdateData = {
        mp_category: formData.mp_category,
        medical_license_number: formData.medical_license_number,
        medical_license_expiry: formData.medical_license_expiry,
        npi_number: formData.npi_number,
        drivers_license_number: formData.drivers_license_number,
        drivers_license_expiry: formData.drivers_license_expiry,
        vehicle_number: formData.vehicle_number,
        vehicle_type: formData.vehicle_type,
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        license_plate: formData.license_plate,
        geographic_area: geographic_area,
        is_active: formData.is_active,
        can_work_weekends: formData.can_work_weekends,
        can_work_evenings: formData.can_work_evenings,
        max_daily_orders: parseInt(formData.max_daily_orders) || 10,
      };

      // Clean the data to handle empty strings properly
      const updateData = cleanUpdateData(rawUpdateData);


      const response =
        await medicalProfessionalsService.updateMedicalProfessional(
          id,
          updateData
        );


      setMedicalProfessional(response);
      setSuccess("Medical professional information updated successfully");

      // Redirect to detail view after successful update
      setTimeout(() => {
        navigate(`/medical-professionals/${id}`);
      }, 1500);
    } catch (err) {
      console.error("Error updating medical professional:", err);

      // FIXED: Better error handling
      let errorMessage = "Failed to update medical professional information";

      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail
            .map((e) => e.msg || e.message)
            .join(", ");
        } else {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      nurse: "primary",
      doctor: "secondary",
      technician: "info",
      phlebotomist: "success",
      other: "default",
    };
    return colors[category?.toLowerCase()] || "default";
  };

  const formatCategory = (category) => {
    if (!category) return "N/A";
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error && !medicalProfessional) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/medical-professionals")}
        >
          Back to Medical Professionals
        </Button>
      </Box>
    );
  }

  if (!medicalProfessional) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Medical professional not found</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/medical-professionals")}
          sx={{ mt: 2 }}
        >
          Back to Medical Professionals
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            component={Link}
            to={`/medical-professionals/${id}`}
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Details
          </Button>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Edit Medical Professional
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Typography variant="h6" color="text.secondary">
                {medicalProfessional.user?.full_name || "Medical Professional"}
              </Typography>
              <Chip
                label={formatCategory(medicalProfessional.mp_category)}
                color={getCategoryColor(medicalProfessional.mp_category)}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid  size={{ xs: 12, md: 8 }}>
            {/* Professional Information */}
            <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Professional Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="mp_category"
                      value={formData.mp_category}
                      onChange={handleInputChange}
                      label="Category"
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Medical License Number"
                    name="medical_license_number"
                    value={formData.medical_license_number}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Medical License Expiry"
                    name="medical_license_expiry"
                    type="date"
                    value={formData.medical_license_expiry}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="NPI Number"
                    name="npi_number"
                    value={formData.npi_number}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Driver's License Number"
                    name="drivers_license_number"
                    value={formData.drivers_license_number}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Driver's License Expiry"
                    name="drivers_license_expiry"
                    type="date"
                    value={formData.drivers_license_expiry}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Starting Point Configuration */}
            <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Starting Point Configuration
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="body2" color="text.secondary" paragraph>
                Configure where this medical professional starts their daily
                routes for better optimization.
              </Typography>

              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Starting Point Name"
                    name="name"
                    value={startingPointData.name}
                    onChange={handleStartingPointChange}
                    placeholder="e.g., Home, Main Office"
                  />
                </Grid>

                <Grid  size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={startingPointData.type}
                      onChange={handleStartingPointChange}
                      label="Type"
                    >
                      <MenuItem value="home">🏠 Home</MenuItem>
                      <MenuItem value="office">🏢 Office</MenuItem>
                      <MenuItem value="facility">🏥 Facility</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid  size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={startingPointData.address}
                    onChange={handleStartingPointChange}
                    placeholder="123 Main St, Los Angeles, CA 90210"
                    helperText="Enter the full address where this medical professional starts their routes"
                  />
                </Grid>

                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Latitude (Optional)"
                    name="lat"
                    type="number"
                    value={startingPointData.lat || ""}
                    onChange={(e) =>
                      setStartingPointData((prev) => ({
                        ...prev,
                        lat: e.target.value ? parseFloat(e.target.value) : null,
                      }))
                    }
                    placeholder="34.0522"
                    inputProps={{ step: "any", min: -90, max: 90 }}
                    helperText="GPS latitude coordinate"
                  />
                </Grid>

                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Longitude (Optional)"
                    name="lng"
                    type="number"
                    value={startingPointData.lng || ""}
                    onChange={(e) =>
                      setStartingPointData((prev) => ({
                        ...prev,
                        lng: e.target.value ? parseFloat(e.target.value) : null,
                      }))
                    }
                    placeholder="-118.2437"
                    inputProps={{ step: "any", min: -180, max: 180 }}
                    helperText="GPS longitude coordinate"
                  />
                </Grid>

                {startingPointData.address && (
                  <Grid  size={{ xs: 12 }}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        📍 {startingPointData.name || startingPointData.address}
                      </Typography>
                      <Typography variant="caption">
                        Address: {startingPointData.address}
                      </Typography>
                      {startingPointData.lat && startingPointData.lng && (
                        <Typography variant="caption" display="block">
                          Coordinates: {startingPointData.lat},{" "}
                          {startingPointData.lng}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          startIcon={<MapIcon />}
                          onClick={() => {
                            const url = `https://www.google.com/maps/search/${encodeURIComponent(
                              startingPointData.address
                            )}`;
                            window.open(url, "_blank");
                          }}
                        >
                          View on Google Maps
                        </Button>
                      </Box>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Work Preferences */}
            <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Work Preferences
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Max Daily Orders"
                    name="max_daily_orders"
                    type="number"
                    value={formData.max_daily_orders}
                    onChange={handleInputChange}
                    inputProps={{ min: 1, max: 20 }}
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="can_work_weekends"
                        checked={formData.can_work_weekends}
                        onChange={handleInputChange}
                        color="primary"
                      />
                    }
                    label="Available for weekend work"
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="can_work_evenings"
                        checked={formData.can_work_evenings}
                        onChange={handleInputChange}
                        color="primary"
                      />
                    }
                    label="Available for evening shifts"
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        color="primary"
                      />
                    }
                    label="Active status"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid  size={{ xs: 12, md: 4 }}>
            {/* Vehicle Information - FIXED */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Vehicle Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid  size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Vehicle Number"
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleInputChange}
                    placeholder="e.g., V001, Fleet-123"
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Vehicle Type</InputLabel>
                    <Select
                      name="vehicle_type"
                      value={formData.vehicle_type}
                      onChange={handleInputChange}
                      label="Vehicle Type"
                    >
                      {vehicleTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Vehicle Make"
                    name="vehicle_make"
                    value={formData.vehicle_make}
                    onChange={handleInputChange}
                    placeholder="e.g., Toyota, Ford"
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Vehicle Model"
                    name="vehicle_model"
                    value={formData.vehicle_model}
                    onChange={handleInputChange}
                    placeholder="e.g., Camry, Explorer"
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="License Plate"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleInputChange}
                    placeholder="e.g., ABC123"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          mt={4}
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "background.default",
            py: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate(`/medical-professionals/${id}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditMedicalProfessional;
